import { useState } from 'react';

import { api, type Template } from '../lib/api';
import { Alert, Card, Field, PageHead, formatTime, useLoader } from '../components/ui';

const BLANK = {
  name: '',
  subject_template: '',
  html_template: '',
  text_template: '',
};

export function Templates() {
  const { data, error, loading, reload } = useLoader(() => api.templates.list());
  const [draft, setDraft] = useState<typeof BLANK | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function startEdit(template: Template) {
    setEditingId(template.id);
    setDraft({
      name: template.name,
      subject_template: template.subject_template ?? '',
      html_template: template.html_template ?? '',
      text_template: template.text_template ?? '',
    });
  }

  async function save(event: React.FormEvent) {
    event.preventDefault();
    if (!draft) return;
    setBusy(true);
    setFormError(null);
    try {
      if (editingId) {
        await api.templates.update(editingId, draft);
      } else {
        await api.templates.create(draft);
      }
      setDraft(null);
      setEditingId(null);
      reload();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Could not save template');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <PageHead
        title="Templates"
        subtitle="Stored subject and body content, editable without a deploy."
        actions={
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              setEditingId(null);
              setDraft(draft ? null : { ...BLANK });
            }}
          >
            {draft && !editingId ? 'Cancel' : 'New template'}
          </button>
        }
      />

      {error && <Alert kind="error">{error}</Alert>}

      <Alert kind="info">
        Templates are stored, not rendered. There is no server-side interpolation engine
        yet, so placeholders like <code>{'{{first_name}}'}</code> are substituted wherever
        you render — which is also why click tracking leaves an unrendered placeholder
        alone rather than baking it into a signed URL.
      </Alert>

      {draft && (
        <Card title={editingId ? 'Edit template' : 'New template'}>
          {formError && <Alert kind="error">{formError}</Alert>}
          <form onSubmit={save}>
            <Field id="tpl-name" label="Name">
              {(props) => (
                <input
                  {...props}
                  required
                  placeholder="welcome-email"
                  value={draft.name}
                  onChange={(event) => setDraft({ ...draft, name: event.target.value })}
                />
              )}
            </Field>
            <Field id="tpl-subject" label="Subject">
              {(props) => (
                <input
                  {...props}
                  value={draft.subject_template}
                  onChange={(event) =>
                    setDraft({ ...draft, subject_template: event.target.value })
                  }
                />
              )}
            </Field>
            <Field id="tpl-html" label="HTML">
              {(props) => (
                <textarea
                  {...props}
                  value={draft.html_template}
                  onChange={(event) => setDraft({ ...draft, html_template: event.target.value })}
                />
              )}
            </Field>
            <Field
              id="tpl-text"
              label="Plain text"
              hint="Always ship a text part. Filters weigh a missing one against you, and text-only readers get a blank message."
            >
              {(props) => (
                <textarea
                  {...props}
                  style={{ minHeight: 80 }}
                  value={draft.text_template}
                  onChange={(event) => setDraft({ ...draft, text_template: event.target.value })}
                />
              )}
            </Field>
            <div className="row">
              <button className="btn btn-primary" disabled={busy}>
                {busy ? 'Saving…' : 'Save template'}
              </button>
              <button
                type="button"
                className="btn"
                onClick={() => {
                  setDraft(null);
                  setEditingId(null);
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </Card>
      )}

      {(data ?? []).map((template) => (
        <Card
          key={template.id}
          title={template.name}
          actions={
            <div className="row">
              <button type="button" className="btn btn-sm" onClick={() => startEdit(template)}>
                Edit
              </button>
              <button
                type="button"
                className="btn btn-sm btn-danger"
                onClick={async () => {
                  if (!window.confirm(`Delete "${template.name}"?`)) return;
                  await api.templates.remove(template.id);
                  reload();
                }}
              >
                Delete
              </button>
            </div>
          }
        >
          <div className="field">
            <div className="tile-label">Subject</div>
            <div style={{ marginTop: 3 }}>{template.subject_template || '—'}</div>
          </div>
          {template.html_template && (
            <div className="field">
              <div className="tile-label">HTML preview</div>
              {/*
                Rendered in a sandboxed iframe with no allow-* flags: this is
                customer-authored HTML, and injecting it into the dashboard's own
                document would hand it our session cookie via any script it carries.
              */}
              <iframe
                title={`${template.name} preview`}
                sandbox=""
                srcDoc={template.html_template}
                style={{
                  width: '100%',
                  height: 180,
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-sm)',
                  background: '#fff',
                  marginTop: 5,
                }}
              />
            </div>
          )}
          <div className="hint">Updated {formatTime(template.updated_at)}</div>
        </Card>
      ))}

      {(data ?? []).length === 0 && !draft && (
        <Card>
          <div className="empty">{loading ? 'Loading…' : 'No templates yet.'}</div>
        </Card>
      )}
    </>
  );
}
